import { defaultAxiosInstance } from '../config/axiosConfig';

/**
 * Platform MCP ("Quadroid") — the one shared MCP server users connect AI apps
 * to with a personal connection key. Keys themselves live in mcpKeysService.
 *
 * Every call uses the signed-in user's normal x-api-key; a connection key
 * (`fmk.…`) is refused here with 401.
 */

/** Where each tier reads activity from. Same response shape everywhere. */
const ACTIVITY_PATHS = {
  user: '/user/platform-mcp/activity',
  company: '/company-admin/platform-mcp/activity',
  partner: '/partner-admin/platform-mcp/activity',
  platform: '/platform/platform-mcp/activity',
};

const hasValue = (v) => v !== null && v !== undefined && v !== '';

const compactParams = (params = {}) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => hasValue(v)));

class PlatformMcpService {
  /**
   * GET /user/platform-mcp → { server_ready, url, connect, tools, keys }.
   * `server_ready:false` → `url`/`connect` null, `tools` empty. 403 without
   * the `platform_mcp` permission.
   */
  async getPage() {
    const res = await defaultAxiosInstance.get('/user/platform-mcp');
    return res.data;
  }

  /**
   * Activity for one tier. Params (all optional): window | start + end
   * (end exclusive), tz, status (ok|error|cancelled), tool, limit, offset,
   * and per tier company_id / partner_id / user_id.
   * → { calls, total, summary: { calls, errors }, range }
   */
  async listActivity(scope, params = {}) {
    const path = ACTIVITY_PATHS[scope];
    if (!path) throw new Error(`Unknown activity scope: ${scope}`);
    const res = await defaultAxiosInstance.get(path, { params: compactParams(params) });
    return res.data;
  }

  // ── Platform admin ──

  /** GET /platform/platform-mcp → status; `{exists:false, update_available:true}` before setup. */
  async getStatus() {
    const res = await defaultAxiosInstance.get('/platform/platform-mcp');
    return res.data;
  }

  /**
   * POST /platform/platform-mcp/setup (no body). Creates the server the first
   * time, afterwards syncs its tools. → { created, status, published_now, url,
   * tools_enabled, tools_total, added, disabled_by_sync, import_notes, lint, notice? }
   * 409 while another setup runs; 400 when a server setting is missing.
   */
  async setup() {
    const res = await defaultAxiosInstance.post('/platform/platform-mcp/setup');
    return res.data;
  }

  /** PATCH /platform/platform-mcp — `{ published }` and/or `{ rate_limit_per_min }` (0 = default). */
  async update(patch) {
    const res = await defaultAxiosInstance.patch('/platform/platform-mcp', patch);
    return res.data;
  }

  /** GET /platform/platform-mcp/tools → { tools: [{ id, enabled, name, title, description, read_only }], total } */
  async listTools() {
    const res = await defaultAxiosInstance.get('/platform/platform-mcp/tools');
    return res.data;
  }

  /** PATCH /platform/platform-mcp/tools/{id} → the updated tool row. */
  async setToolEnabled(id, enabled) {
    const res = await defaultAxiosInstance.patch(`/platform/platform-mcp/tools/${id}`, { enabled });
    return res.data;
  }
}

const platformMcpService = new PlatformMcpService();
export default platformMcpService;
